import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FaIcon } from '@fe-learner-common';
import { IntlShape, useIntl } from 'react-intl';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { faLanguage } from '@fortawesome/pro-regular-svg-icons/faLanguage';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faIcons } from '@fortawesome/pro-regular-svg-icons/faIcons';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons/faCalendarDays';
import {
  toHumanHoursAndMinutes,
  toLocaleDate,
} from '@fe-learner-common/utils/date';
import { TCategory } from '@api/io/categories.io';
import { FC } from 'react';
import { OverviewSessionDetails } from '@components/tabs/overview/OverviewTab';

type Detail = {
  icon: IconDefinition;
  titleKey: string;
  value: string;
};
type Details =
  | 'language'
  | 'timeZone'
  | 'duration'
  | 'categories'
  | 'enrollmentPeriod'
  | 'enrollmentCurrent'
  | 'enrollmentMax';

const detailsMap: Record<
  Details,
  ({
    sessionDetails,
    intl,
  }: {
    sessionDetails: OverviewSessionDetails;
    intl?: IntlShape;
    categories?: Map<number, TCategory>;
    collectionName?: string;
  }) => Detail | null | undefined
> = {
  language: ({ sessionDetails }) =>
    sessionDetails.language
      ? {
          icon: faLanguage,
          titleKey: 'learner.common.language',
          value: sessionDetails.language,
        }
      : null,

  timeZone: ({ sessionDetails }) =>
    sessionDetails.timezone
      ? {
          icon: faGlobe,
          titleKey: 'learner.common.timezone',
          value: sessionDetails.timezone,
        }
      : null,

  enrollmentPeriod: ({ sessionDetails }) =>
    sessionDetails.enrollment?.from || sessionDetails.enrollment?.to
      ? {
          icon: faCalendarDays,
          titleKey: 'catalog.course.overview.enrollment.period',
          value: `${sessionDetails.enrollment?.from ? toLocaleDate(sessionDetails.enrollment.from * 1000) + ' -' : '-'} ${sessionDetails.enrollment?.to ? toLocaleDate(sessionDetails.enrollment.to * 1000) : ''}`,
        }
      : null,

  duration: ({ sessionDetails }) => {
    if (!sessionDetails.duration || sessionDetails.duration < 60) return null;

    const durationFormatted = toHumanHoursAndMinutes(sessionDetails.duration);
    if (!durationFormatted) return null;

    return {
      icon: faClock,
      titleKey: 'learner.common.duration',
      value: durationFormatted,
    };
  },

  categories: ({ sessionDetails, categories }) =>
    categories?.size && sessionDetails.categories?.length
      ? {
          icon: faIcons,
          titleKey: 'learner.common.categories',
          value: sessionDetails.categories
            .map((categoryId) => categories?.get(categoryId)?.name)
            .join(', '),
        }
      : null,
  enrollmentCurrent: ({ sessionDetails }) =>
    sessionDetails.enrollment?.current?.show &&
    !!sessionDetails.enrollment.current?.limit
      ? {
          icon: faUser,
          titleKey: 'catalog.course.overview.enrollment.current',
          value: sessionDetails.enrollment.current.limit.toString(),
        }
      : null,
  enrollmentMax: ({ sessionDetails }) =>
    sessionDetails.enrollment?.maximum?.show &&
    !!sessionDetails.enrollment.maximum?.limit
      ? {
          icon: faUser,
          titleKey: 'catalog.course.overview.enrollment.max',
          value: sessionDetails.enrollment.maximum.limit.toString(),
        }
      : null,
};

const Detail = ({ detail }: { detail?: Detail | null }) => {
  const intl = useIntl();
  if (!detail) return null;
  const { icon, titleKey, value } = detail;

  return (
    <div className="flex gap-4">
      <FaIcon icon={icon} width={20} />
      <div className="space-y-1">
        <h4 className="text-label-md">
          {intl.formatMessage({ id: titleKey })}
        </h4>
        <p className="text-body-md text-neutral-500">{value}</p>
      </div>
    </div>
  );
};

type DetailsProps = {
  sessionDetails: OverviewSessionDetails;
  categories?: Map<number, TCategory>;
  collectionName?: string;
};

export const Details: FC<DetailsProps> = ({
  sessionDetails,
  categories,
  collectionName,
}) => {
  const intl = useIntl();
  const mapInput = { sessionDetails, intl, categories, collectionName };
  return (
    <section className="space-y-4">
      <h3 className="text-title-md">
        {intl.formatMessage({ id: 'catalog.course.overview.details' })}
      </h3>
      <div className="grid grid-cols-2 gap-x-5 gap-y-4">
        {Object.entries(detailsMap).map(([key, value]) => (
          <Detail detail={value(mapInput)} key={key} />
        ))}
      </div>
    </section>
  );
};
